import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getPrescriptionslatestbyappt } from "../../../../admin/ApiCalls/prescriptionApis";
import { cachedApiCall, getCacheKey, getCachedData, setCachedData } from "utils/apiCache";

// Global cache for prescription data to share across hook instances
const globalPrescriptionCache = new Map();
const globalFetchPromises = new Map();

// Allow consumers to clear cached prescription data (per appointment or entirely)
export const clearAppointmentPrescriptionCache = (appointmentId) => {
  if (appointmentId) {
    globalPrescriptionCache.delete(appointmentId);
    globalFetchPromises.delete(appointmentId);
    return;
  }
  globalPrescriptionCache.clear();
  globalFetchPromises.clear();
};

/**
 * Centralized fetch for appointment-scoped prescription data.
 * Fetches once per appointment, caches results, and exposes a normalized payload
 * so downstream sections can hydrate without firing duplicate API calls.
 * 
 * OPTIMIZED: Uses global caching and request deduplication.
 */
export const useAppointmentPrescription = (appointmentId) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasFetchedRef = useRef(null);

  const normalize = useCallback((response) => {
    // The API helper already returns response.data; still unwrap defensively.
    const outer = response?.data ?? response ?? {};
    const payload = outer?.data ?? outer;

    const prescription = payload?.prescription ?? {};

    return {
      raw: response ?? {},
      data: payload ?? {},
      prescription,
      medicines: payload?.medicines ?? prescription?.medicines ?? [],
      allergies: payload?.allergies ?? prescription?.allergies ?? [],
      complaints: payload?.complaints ?? [],
      diagnosis: payload?.diagnosis ?? [],
      advice: payload?.advice ?? payload?.advices ?? [],
      clinical_findings: payload?.clinical_findings ?? [],
      referred_to: payload?.referred_to ?? [],
      parameters: payload?.parameters ?? [],
    };
  }, []);

  const fetchPrescription = useCallback(
    async (force = false) => {
      if (!appointmentId) return null;

      const cacheKey = `prescription-by-appt-${appointmentId}`;

      // Check global cache first (unless force refresh)
      if (!force && globalPrescriptionCache.has(appointmentId)) {
        console.log(`[useAppointmentPrescription] Using cached data for appointment ${appointmentId}`);
        const cached = globalPrescriptionCache.get(appointmentId);
        setData(cached);
        return cached;
      }

      // Check if there's already a fetch in progress for this appointment
      if (!force && globalFetchPromises.has(appointmentId)) {
        console.log(`[useAppointmentPrescription] Waiting for in-flight request for appointment ${appointmentId}`);
        try {
          const result = await globalFetchPromises.get(appointmentId);
          setData(result);
          return result;
        } catch (e) {
          // If in-flight request failed, continue to make a new request
        }
      }

      setLoading(true);
      setError(null);

      // Create the fetch promise
      const fetchPromise = (async () => {
        console.log(`[useAppointmentPrescription] Fetching prescription for appointment ${appointmentId}`);
        const response = await getPrescriptionslatestbyappt(appointmentId);
        const normalized = normalize(response);

        // Update global cache
        globalPrescriptionCache.set(appointmentId, normalized);

        return normalized;
      })();

      // Register in-flight promise
      globalFetchPromises.set(appointmentId, fetchPromise);

      try {
        const result = await fetchPromise;
        setData(result);
        return result;
      } catch (err) {
        console.error(`[useAppointmentPrescription] Error fetching prescription:`, err);
        setError(err);
        return null;
      } finally {
        setLoading(false);
        globalFetchPromises.delete(appointmentId);
      }
    },
    [appointmentId, normalize],
  );

  useEffect(() => {
    // Reset local state when appointment changes to avoid stale data flashes.
    setData(null);
    setError(null);
    setLoading(false);

    // Only fetch if we haven't fetched for this appointment yet
    if (appointmentId && hasFetchedRef.current !== appointmentId) {
      hasFetchedRef.current = appointmentId;
      fetchPrescription();
    }
  }, [appointmentId, fetchPrescription]);

  const status = useMemo(
    () => ({
      data,
      loading,
      error,
      refetch: () => fetchPrescription(true), // Force refresh when called manually
    }),
    [data, loading, error, fetchPrescription],
  );

  return status;
};
